/**
 * Drawing methods of the engine, put on it like the layers
 * (`Object.assign(engine, createDrawingApi(engine))`). Changes come through
 * engine.events.onDrawingsChange.
 *
 * @param {object} engine - ChartEngine: drawings (createDrawings)
 */
export const createDrawingApi = (engine) => ({
  /**
   * Puts a drawing of the tool on the chart at its default places (po-chart-visual-spec.md §7.5), not selected.
   *
   * @param {string} toolId - DRAWING_TOOL value (drawings/drawingTools.js)
   * @returns {string|null} its id; null for an unknown tool or a chart without bars
   */
  addDrawing: (toolId) => engine.drawings.create(toolId),

  /** @param {string|null} id - null removes the selection */
  selectDrawing: (id) => engine.drawings.select(id),

  /** @returns {boolean} false for an unknown id */
  removeDrawing: (id) => engine.drawings.remove(id),

  /** Removes every drawing of the user; position lines stay (a layer of their own). */
  removeAllDrawings: () => engine.drawings.removeAll(),

  /**
   * @param {string} id
   * @param {{ color?: string, width?: number }} change - CSS colour, CSS px
   * @returns {boolean} false for an unknown id
   */
  setDrawingStyle: (id, change) => engine.drawings.setStyle(id, change),

  /**
   * Save of the settings modal; a hidden drawing stays in getDrawings and loses the selection.
   *
   * @param {string} id
   * @param {{ style: object, isVisible: boolean }} settings - style: colour, width and the fields of drawings/drawingStyles.js
   * @returns {boolean} false for an unknown id
   */
  updateDrawing: (id, settings) => engine.drawings.updateSettings(id, settings),

  /**
   * A locked drawing can be selected and edited, but the pointer does not move it.
   *
   * @param {string} id
   * @param {boolean} isLocked
   * @returns {boolean} false for an unknown id
   */
  setDrawingLocked: (id, isLocked) => engine.drawings.setLocked(id, isLocked),

  /**
   * Puts an unlocked copy 20 px above the drawing (under it without room above) and selects it.
   *
   * @param {string} id
   * @returns {string|null} id of the copy; null for an unknown id
   */
  cloneDrawing: (id) => engine.drawings.clone(id),

  /** @returns {object[]} id, type, points [{ time (ms), price }], style (drawingStyles.js), isLocked, isVisible */
  getDrawings: () => engine.drawings.list(),
});
